<template>
  <div>
    <video ref="video" preload="metadata" class="video-js vjs-16-9 vjs-theme-fantasy vjs-big-play-centered" controls playsinline webkit-playsinline></video>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import videojs from "video.js";
import video_ko from "../utils/videojs-ko.json";
import "videojs-hotkeys";

// videojs css
import "@/static/css/videojs.css";
import "@videojs/themes/dist/fantasy/index.css";

// videojs plugin css
import "videojs-seek-buttons/dist/videojs-seek-buttons.css";

// videojs plugin js
require("videojs-contrib-quality-levels");
require("videojs-hls-quality-selector");
require("videojs-seek-buttons");

const DEFAULT_EVENTS = ["loadeddata", "canplay", "canplaythrough", "play", "pause", "waiting", "playing", "ended", "error", "airPlayRequested"];

// pollfill
if (typeof Object.assign != "function") {
  Object.defineProperty(Object, "assign", {
    value(target, varArgs) {
      if (target == null) {
        throw new TypeError("Cannot convert undefined or null to object");
      }
      const to = Object(target);
      for (let index = 1; index < arguments.length; index++) {
        const nextSource = arguments[index];
        if (nextSource != null) {
          for (const nextKey in nextSource) {
            if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
              to[nextKey] = nextSource[nextKey];
            }
          }
        }
      }
      return to;
    },
    writable: true,
    configurable: true,
  });
}
export default {
  name: "video-player",
  props: {
    options: {
      type: Object,
      default() {
        return {};
      },
    },
    events: {
      type: Array,
      default: () => [],
    },
    globalEvents: {
      type: Array,
      default: () => [],
    },
    crossOrigin: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      player: null,
      cntntsMap: {},
      playsinline: true,
    };
  },
  watch: {
    $route: function (to, from) {},
  },
  mounted() {
    if (!this.player && this.options != null) {
      this.initialize();
    }
  },
  methods: {
    initialize() {
      videojs.addLanguage("ko", video_ko);

      // ios fullscreen
      this.$refs.video.setAttribute("x5-playsinline", this.playsinline);
      this.$refs.video.setAttribute("x5-video-player-type", "h5");
      this.$refs.video.setAttribute("x5-video-player-fullscreen", false);

      // cross origin
      if (this.crossOrigin !== "") {
        this.$refs.video.crossOrigin = this.crossOrigin;
        this.$refs.video.setAttribute("crossOrigin", this.crossOrigin);
      }

      var option = {
        playsinline: true,
        controls: true,
        autoplay: true,
        language: "ko",
        userActions: {
          hotkeys: true,
        },
        controlBar: {
          volumePanel: { inline: false },
          pictureInPictureToggle: false,
        },
        playbackRate: 1,
        playbackRates: [0.5, 1, 1.5, 2],
        html5: {
          hls: {
            overrideNative: !videojs.browser.IS_ANY_SAFARI || !videojs.browser.IS_IOS,
          },
          vhs: {
            overrideNative: !videojs.browser.IS_ANY_SAFARI || !videojs.browser.IS_IOS,
          },
          nativeAudioTracks: videojs.browser.IS_ANY_SAFARI || videojs.browser.IS_IOS,
          nativeVideoTracks: videojs.browser.IS_ANY_SAFARI || videojs.browser.IS_IOS,
          nativeTextTracks: videojs.browser.IS_ANY_SAFARI || videojs.browser.IS_IOS,
        },
        techOrder: ["html5"],
        plugins: {},
        sources: this.options.src,
      };

      // mobile hide full screen
      var userAgent = navigator.userAgent || navigator.vendor || window.opera;
      if (/khsaeduapp/i.test(userAgent)) {
        options.controlBar.fullscreenToggle = false;
      }
      if (/iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) || /Android/i.test(userAgent)) {
        //options.controlBar.fullscreenToggle = false;
      }

      // emit event
      const emitPlayerState = (event, value) => {
        if (event) {
          this.$emit(event, this.player);
        }
      };

      const videoOptions = Object.assign({}, option, this.options);
      // avoid error "VIDEOJS: ERROR: Unable to find plugin: __ob__"
      if (videoOptions.plugins) {
        delete videoOptions.plugins.__ob__;
      }

      const self = this;
      this.player = videojs(this.$refs.video, videoOptions, function () {
        // events
        const events = DEFAULT_EVENTS.concat(self.events).concat(self.globalEvents);
        // watch events
        const onEdEvents = {};
        for (let i = 0; i < events.length; i++) {
          if (typeof events[i] === "string" && onEdEvents[events[i]] === undefined) {
            ((event) => {
              onEdEvents[event] = null;
              this.on(event, () => {
                emitPlayerState(event, true);
              });
            })(events[i]);
          }
        }

        this.hotkeys({
          volumeStep: 0.1,
          seekStep: 5,
          enableMute: true,
          enableFullscreen: true,
          enableNumbers: false,
          enableVolumeScroll: true,
          enableHoverScroll: true,
          captureDocumentHotkeys: true,
          documentHotkeysFocusElementFilter: (e) => e.tagName.toLowerCase() === "body",
        });

        this.el()
          .querySelector(".vjs-control-bar")
          .addEventListener("click", (event) => {
            console.log("blur");
            this.blur();
          });

        this.el()
          .querySelector(".vjs-control-bar")
          .addEventListener("keyup", (event) => {
            console.log(" keyup blur", event.keyCode);
            if (event.keyCode != 9) this.el().querySelector("video").focus();
          });

        // watch timeupdate
        this.on("timeupdate", function () {
          emitPlayerState("timeupdate", this.currentTime());
        });

        this.on("userinactive", function (e) {
          emitPlayerState("userinactive", e);
        });

        this.on("useractive", function (e) {
          emitPlayerState("useractive", e);
        });

        // player readied
        self.$emit("ready", this);
      });

      if (window.WebKitPlaybackTargetAvailabilityEvent) {
        this.player
          .el()
          .querySelector("video, audio")
          .addEventListener("webkitcurrentplaybacktargetiswirelesschanged", function (event) {
            // self.player.options_.html5.hls.overrideNative = false;
            // self.player.options_.html5.vhs.overrideNative = false;
          });
      }

      // configure plugins
      /*
      this.player.landscapeFullscreen({
        fullscreen: {
          enterOnRotate: true,
          exitOnRotate: true,
          alwaysInLandscapeMode: true,
          iOS: true,
        },
      });
      */

      this.player.hlsQualitySelector({
        displayCurrentQuality: false,
        vjsIconClass: "vjs-icon-hd",
      });

      this.player.seekButtons({
        forward: 5,
        back: 5,
      });

      this.player.on("loadedmetadata", function () {
        //if (videoOptions.currentTime != null && Number(videoOptions.currentTime) > 0) this.currentTime(Number(videoOptions.currentTime));

        // detect ios and caption hide (if not exists caption)
        if (/iPad|iPhone|iPod/.test(navigator.platform) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) {
          if (this.textTracks().length == 0) {
            document.querySelector(".vjs-subs-caps-button").style = "display:none";
          }
        }

        var captionLangCd = self.$cookies.get("PLAY_CAPTION_LANG") || "kor";
        var textTracks = this.textTracks();
        if (textTracks != null && textTracks.length > 0) {
          for (var i = 0; i < textTracks.length; i++) {
            var track = textTracks[i];
            if (captionLangCd.includes(this.textTracks()[i].language)) {
              this.textTracks()[i].enabled = true;
            }

            this.textTracks()[i].default = false;
            this.textTracks()[i].mode = "disabled";

            if (track.label == "ENGLISH") {
              this.textTracks()[i].language = "eng";
              this.textTracks()[i].label = "English";
            }
            if (track.label == "KOREAN") {
              this.textTracks()[i].language = "kor";
              this.textTracks()[i].label = "한국어";
            }
            if (track.label == "CHINESE") {
              this.textTracks()[i].language = "zho";
              this.textTracks()[i].label = "中文";
            }
          }

          this.textTracks().on("change", (e) => {
            var tracks = this.textTracks();
            for (var i = 0; i < tracks.length; i++) {
              if (tracks[i].mode === "showing") {
                this.currentCaption = tracks[i].language;

                self.$cookies.set("PLAY_CAPTION_LANG", this.currentCaption);
                emitPlayerState("captionchange", tracks[i].language);
                return;
              }
            }
          });
          this.textTracks().trigger("change");
        }

        var audioLangCd = self.$cookies.get("PLAY_AUDIO_LANG") || "kor";
        var audioTracks = this.audioTracks();
        if (audioTracks != null && audioTracks.length > 0) {
          for (var i = 0; i < audioTracks.length; i++) {
            var track = audioTracks[i];
            if (track.label == "ENGLISH") {
              this.audioTracks()[i].language = "eng";
              this.audioTracks()[i].label = "English";
            }
            if (track.label == "KOREAN") {
              this.audioTracks()[i].language = "kor";
              this.audioTracks()[i].label = "한국어";
            }
            if (track.label == "CHINESE") {
              this.audioTracks()[i].language = "zho";
              this.audioTracks()[i].label = "中文";
            }

            if (audioLangCd.includes(this.audioTracks()[i].language)) {
              this.audioTracks()[i].enabled = true;
            }
          }

          this.audioTracks().on("change", (e) => {
            var tracks = this.audioTracks();
            for (var i = 0; i < tracks.length; i++) {
              if (tracks[i].enabled) {
                this.currentAudio = tracks[i].language;

                self.$cookies.set("PLAY_AUDIO_LANG", this.currentAudio);
                emitPlayerState("audiochange", tracks[i].language);
                return;
              }
            }
          });
          this.audioTracks().trigger("change");
        }

        let qualityLevels = this.qualityLevels();
        // console.log(qualityLevels);
        qualityLevels.selectedIndex_ = 0;
        qualityLevels.on("change", (e) => {
          this.currentQuality = this.qualityLevels()[e.selectedIndex].height;
          emitPlayerState("qualitychange", this.currentQuality);
        });

        var playbackRate = Number(self.$cookies.get("PLAY_PLAYBACK_RATE") || "1");
        this.playbackRate(playbackRate);
      });

      this.player.on("ratechange", (e, a) => {
        self.$cookies.set("PLAY_PLAYBACK_RATE", this.player.playbackRate());
      });
    },
    dispose(callback) {
      if (this.player && this.player.dispose) {
        this.player.pause && this.player.pause();
        this.player.dispose();
        this.player = null;

        this.$nextTick(() => {
          this.reseted = false;
          this.$nextTick(() => {
            this.reseted = true;
            this.$nextTick(() => {
              callback && callback();
            });
          });
        });
      }
    },
  },
  beforeDestroy() {
    if (this.player) {
      this.dispose();
    }
  },
  computed: {
    ...mapGetters(["currentUser"]),
    ownPost() {
      return this.post.user.id === (this.currentUser && this.currentUser.id);
    },
  },
  watch: {
    options: {
      deep: true,
      handler(options, oldOptions) {
        if (options) {
          this.initialize();
        }
      },
    },
  },
};
</script>
